import {
    differenceInDays,
    format,
    set,
    setSeconds,
    startOfDay,
} from 'date-fns';

import { AuthService } from '../../auth/auth.service';
import { DispositionReason } from '../../coaching-base/config';
import {
    CoachingEventStatus,
    CoachingRole,
    CommitmentApprovalDto,
    CommitmentDraftDto,
    CommitmentDto,
    CommitmentEvent,
    DraftAndApprovalResult,
    FocusDto,
    GoalStatus,
} from '../../coaching-base/general';
import { epicDependencies } from '../../store';
import { TranslationKeyMap } from '../coaching-commitment.i18n';
import { dateSort } from '../coaching-commitment.model';
import { createTagInfo } from '../functions';
import { GoalsCollection } from '../goals-collection.model';

import { allowedAndEnabledActionsReducer } from './allowed-and-enabled-actions.reducer';
import { RootState } from './commitment.state';

/** @internal only exported for testing */
export function determineActiveDays(
    statusId: CoachingEventStatus,
    activeDate: Date,
    closedDate: Date,
): number {
    const exclude = [
        CoachingEventStatus.DRAFT,
        CoachingEventStatus.PRE_APPROVAL,
        CoachingEventStatus.UPDATING,
    ];
    if (exclude.includes(statusId)) {
        return 0;
    }

    const emptyHours = {
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
    };
    const activeFinal = set(activeDate || new Date(), emptyHours);
    const closedFinal = set(closedDate || new Date(), emptyHours);
    return differenceInDays(closedFinal, activeFinal);
}

const coachingEventStatusTranslationMap: {
    [key in CoachingEventStatus]: string;
} = {
    [CoachingEventStatus.DRAFT]: TranslationKeyMap._9476_DRAFT,
    [CoachingEventStatus.PRE_APPROVAL]:
        TranslationKeyMap._9791_PENDING_APPROVAL,
    [CoachingEventStatus.ACTIVE]: TranslationKeyMap._125_ACTIVE,
    [CoachingEventStatus.UPDATING]: TranslationKeyMap._9791_PENDING_APPROVAL,
    [CoachingEventStatus.PENDING_CLOSE]: TranslationKeyMap._7441_PENDING_CLOSE,
    [CoachingEventStatus.CLOSED]: TranslationKeyMap._7440_CLOSED,
    [CoachingEventStatus.DISPUTED]: TranslationKeyMap._9476_DRAFT,
    [CoachingEventStatus.DELETED]: TranslationKeyMap._9496_DELETED,
};

/** @internal only exported for testing */
export function determineStatusLabels(
    statusId: CoachingEventStatus,
    draftAndApproval: DraftAndApprovalResult,
    draft: CommitmentDraftDto,
    approval: CommitmentApprovalDto,
    authUserId: number,
    dispositionReasons: DispositionReason[],
): { statusName: string; subStatusName: string; subStatusMessage: string } {
    let statusName = coachingEventStatusTranslationMap[statusId];
    let subStatusName: string;
    let subStatusMessage: string;

    if (
        statusId === CoachingEventStatus.ACTIVE &&
        draftAndApproval &&
        draftAndApproval.isOriginalCommitment &&
        draftAndApproval.draftId
    ) {
        statusName = TranslationKeyMap._9814_ACTIVE__WITH_A_DRAFT_VERSION_;
    }

    if (
        statusId === CoachingEventStatus.ACTIVE &&
        !draftAndApproval &&
        draft &&
        draft.draftId
    ) {
        statusName = TranslationKeyMap._9816_DRAFT__WITH_AN_ACTIVE_VERSION_;
    }

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (draftAndApproval && draftAndApproval.isOriginalCommitment) {
        if (
            (draftAndApproval.approvalId != null &&
                draftAndApproval.approved == null) ||
            (draftAndApproval.targetedApprovalId != null &&
                draftAndApproval.targetedApproved == null)
        ) {
            statusName = TranslationKeyMap._9791_PENDING_APPROVAL;
        }
        if (
            draftAndApproval.approved === false ||
            (draftAndApproval.approvalId == null &&
                (draftAndApproval.previousApproved === false ||
                    draftAndApproval.targetedApproved === false))
        ) {
            statusName = TranslationKeyMap._9792_BEING_DISPUTED;
        }
    }

    const otherApproval = draft
        ? draft.approval || draft.previousApproval
        : approval;
    if (otherApproval && otherApproval.approved === false) {
        if (
            otherApproval.diff.previousStatusId === CoachingEventStatus.DRAFT ||
            otherApproval.diff.previousStatusId ===
                CoachingEventStatus.PRE_APPROVAL ||
            otherApproval.diff.previousStatusId === CoachingEventStatus.UPDATING
        ) {
            statusName = TranslationKeyMap._9476_DRAFT;
            subStatusName =
                otherApproval.targetId === authUserId
                    ? TranslationKeyMap._9793_DISPUTED_BY_ME
                    : TranslationKeyMap._8703_DISPUTED;
        }
        if (
            otherApproval.diff.previousStatusId ===
            CoachingEventStatus.PENDING_CLOSE
        ) {
            statusName = TranslationKeyMap._7441_PENDING_CLOSE;
            subStatusName =
                otherApproval.targetId === authUserId
                    ? TranslationKeyMap._9794_DISPUTED_BY_ME_
                    : TranslationKeyMap._9795_DISPUTED_;

            const dr = dispositionReasons.find(
                (e) =>
                    e.dispositionReasonId === otherApproval.state.closeReasonId,
            );
            subStatusMessage = dr
                ? dr.dispositionReasonName
                : otherApproval.state.closeReasonName;
        }
    }

    return { statusName, subStatusName, subStatusMessage };
}

export function commitmentDistillReducer(
    state: RootState,
    approval: CommitmentApprovalDto,
    commitment: CommitmentDto,
    goalMetricIdList: number[],
    goalsCollection: GoalsCollection,
): RootState {
    // TODO revise this block
    if (goalMetricIdList && goalMetricIdList.length > 0) {
        goalMetricIdList.forEach((x) => {
            if (
                goalsCollection.commitmentKpis.filter((y) => y.metricId === x)
                    .length < 1
            ) {
                goalsCollection.commitmentKpis.unshift({
                    metricId: x,
                    metricName: state.metrics.find((y) => y.id === x).name,
                    statusId: GoalStatus.DRAFT,
                });
            }
        });
    }

    const performer = commitment.latestEventParticipants.find(
        (e) => e.coachingRoleId === CoachingRole.PERFORMER,
    );
    const coach = commitment.latestEventParticipants.find(
        (e) => e.coachingRoleId === CoachingRole.COACH,
    );
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const latestEvent = commitment.latestEvent || ({} as CommitmentEvent);

    const nextFocus: FocusDto = {
        focusId: commitment.focus.focusId,
        userId: commitment.focus.userId,
        behaviourId: commitment.focus.behaviourId,
        subBehaviourId: commitment.focus.subBehaviourId,
        metrics: null,
    };
    const tagInfo = createTagInfo(
        nextFocus,
        state.behaviours,
        state.subBehaviours,
    );

    const linkedObservations =
        dateSort('event.sessionDate', commitment.observations) || [];

    const activeDays = determineActiveDays(
        commitment.statusId,
        commitment.activeDate,
        commitment.closedDate,
    );

    // TODO revise. what if time elapses > 24 hours?
    const totalTime = setSeconds(
        startOfDay(new Date()),
        commitment.totalDuration,
    );
    const totalTimeStr = format(totalTime, 'H:mm:ss');

    /// /////// separator //////////

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const isReadOnly =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        commitment.draftAndApproval &&
        commitment.draftAndApproval.isOriginalCommitment &&
        (commitment.draftAndApproval.approved === false ||
            commitment.draftAndApproval.previousApproved === false ||
            commitment.draftAndApproval.targetedApproved === false);
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const originalWithDraft =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        commitment.draftAndApproval &&
        commitment.draftAndApproval.isOriginalCommitment &&
        !!commitment.draftAndApproval.draftId;

    // commitment.draftAndApproval is null when the commitment is retrieved via an approvalId
    // TODO FIXME. this can be done better. just to get current user id
    const service = epicDependencies.injector.get<AuthService>(AuthService);
    const authUserId = service.user.id;
    const isReadWrite =
        approval &&
        approval.approved === false &&
        /* TODO revise. not sure its for sender only */
        approval.fromUserId === authUserId;

    /// /////// separator //////////

    let creatorName =
        performer && performer.id === authUserId
            ? performer.fullName
            : coach
            ? coach.fullName
            : null;
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (
        commitment.commitmentId != null ||
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        (approval && approval.draftId != null)
    ) {
        creatorName = `${commitment.createdByFirstName} ${commitment.createdByLastName}`;
    }

    const { statusName, subStatusName, subStatusMessage } =
        determineStatusLabels(
            commitment.statusId,
            commitment.draftAndApproval,
            state.draft,
            approval,
            authUserId,
            state.coachingEvent.dispositionReasons,
        );

    const nextState: RootState = {
        ...state,
        commitment,
        event: latestEvent,
        performer,
        coach,
        creatorName,
        tagInfo,
        statusName,
        subStatusName,
        subStatusMessage,
        activeDays,
        totalTimeStr,
        //
        comments: commitment.notes,
        linkedObservations,
        goalsCollection,
        isEditing: isReadOnly || originalWithDraft ? false : state.isEditing, // override to false when read only
        isReadOnly,
        isReadWrite,
    };

    return allowedAndEnabledActionsReducer(nextState);
}
